import { useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { Brain, ShieldCheck } from "@phosphor-icons/react";

export default function AILogs() {
  const [tab, setTab] = useState("ai"); // ai | admin

  return (
    <>
      <PageHeader
        label="Auditabilidad"
        title="Auditoría IA & Acciones"
        description="Cada decisión IA y cada acción de escritura del panel queda registrada para trazabilidad y compliance."
      />
      <PageBody>
        <div className="flex items-center gap-1 mb-4 border-b border-carbon-200">
          {[
            { k: "ai", label: "Decisiones IA", icon: Brain },
            { k: "admin", label: "Acciones de usuarios", icon: ShieldCheck },
          ].map(({ k, label, icon: Icon }) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`audit-tab-${k}`}
              className={`px-4 py-2 text-sm flex items-center gap-2 border-b-2 -mb-px ${tab === k ? "border-terracotta text-terracotta font-medium" : "border-transparent text-carbon-500 hover:text-carbon-700"}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        {tab === "ai" ? <AIDecisionsTab /> : <AdminActionsTab />}
      </PageBody>
    </>
  );
}

function AIDecisionsTab() {
  const [filters, setFilters] = useState({ decision_type: "", min_confidence: "" });
  const { data, loading } = useFetch(
    () => api.get("/ai-logs", { params: { ...filters, min_confidence: filters.min_confidence || undefined, decision_type: filters.decision_type || undefined, limit: 200 } }).then((r) => r.data),
    [filters]
  );
  return (
    <>
      <Card className="p-4 mb-4">
        <div className="flex gap-3 flex-wrap">
          <select value={filters.decision_type} onChange={(e) => setFilters({ ...filters, decision_type: e.target.value })} data-testid="filter-decision-type" className="text-sm bg-page border border-carbon-200 rounded px-3 py-2">
            <option value="">Todo tipo de decisión</option>
            <option value="generate_reply">generate_reply</option>
            <option value="classify_intent">classify_intent</option>
            <option value="advance_stage">advance_stage</option>
            <option value="score_user">score_user</option>
          </select>
          <input
            type="number" step="0.1" min="0" max="1"
            value={filters.min_confidence}
            onChange={(e) => setFilters({ ...filters, min_confidence: e.target.value })}
            placeholder="min confidence" data-testid="filter-confidence"
            className="text-sm bg-page border border-carbon-200 rounded px-3 py-2 w-40"
          />
        </div>
      </Card>
      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-carbon-200 text-left">
              <th className="px-5 py-3 section-label">Cuándo</th>
              <th className="px-5 py-3 section-label">Tipo</th>
              <th className="px-5 py-3 section-label">Resumen</th>
              <th className="px-5 py-3 section-label">Confianza</th>
              <th className="px-5 py-3 section-label">Modelo</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-5 py-10 text-center text-carbon-500">Cargando...</td></tr>}
            {!loading && data?.items?.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-carbon-500">Sin logs aún. Genera respuestas IA desde el simulador.</td></tr>
            )}
            {data?.items?.map((l) => (
              <tr key={l.id} className="border-b border-carbon-100 hover:bg-page" data-testid={`log-${l.id}`}>
                <td className="px-5 py-3 font-mono text-xs text-carbon-500">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-5 py-3"><Badge tone="terracotta">{l.decision_type}</Badge></td>
                <td className="px-5 py-3">
                  <div className="text-sm">{l.explanation_summary}</div>
                  {l.output_snippet && <div className="text-xs text-carbon-500 mt-1 italic line-clamp-1">→ {l.output_snippet}</div>}
                </td>
                <td className="px-5 py-3 font-mono text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-1.5 bg-carbon-100 rounded">
                      <div className="h-full bg-forest rounded" style={{ width: `${(l.confidence_score || 0) * 100}%` }} />
                    </div>
                    <span>{l.confidence_score?.toFixed?.(2)}</span>
                  </div>
                </td>
                <td className="px-5 py-3 font-mono text-xs">{l.provider_used}/{l.model_used}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function AdminActionsTab() {
  const [filters, setFilters] = useState({ admin_email: "", method: "", path: "" });
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;

  const { data, loading, reload } = useFetch(
    () => api.get("/admin/audit-logs", {
      params: {
        admin_email: filters.admin_email || undefined,
        method: filters.method || undefined,
        path: filters.path || undefined,
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
      },
    }).then((r) => r.data),
    [filters, page]
  );
  const { data: stats } = useFetch(
    () => api.get("/admin/audit-logs/stats").then((r) => r.data),
    []
  );

  const methodColor = (m) => ({
    POST: "bg-forest/15 text-forest",
    PUT: "bg-terracotta/15 text-terracotta",
    PATCH: "bg-terracotta/15 text-terracotta",
    DELETE: "bg-signal/15 text-signal",
  }[m] || "bg-carbon-100 text-carbon-600");

  const items = data?.items || [];

  return (
    <>
      {/* Stats: top admins + top paths (30d) */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <Card className="p-4">
            <div className="section-label mb-2">Top admins (30 días)</div>
            {(stats.top_admins || []).length === 0 && <div className="text-xs text-carbon-400">Sin actividad reciente.</div>}
            {(stats.top_admins || []).slice(0, 6).map((a) => (
              <div key={a.admin_email} className="flex items-center justify-between text-xs py-1 border-b border-carbon-100 last:border-b-0">
                <span className="font-mono truncate">{a.admin_email}</span>
                <span className="font-mono text-carbon-500">{a.count} acciones</span>
              </div>
            ))}
          </Card>
          <Card className="p-4">
            <div className="section-label mb-2">Endpoints más usados (30 días)</div>
            {(stats.top_paths || []).length === 0 && <div className="text-xs text-carbon-400">—</div>}
            {(stats.top_paths || []).slice(0, 6).map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-carbon-100 last:border-b-0">
                <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${methodColor(p.method)}`}>{p.method}</span>
                <span className="font-mono truncate flex-1">{p.path}</span>
                <span className="font-mono text-carbon-500">{p.count}</span>
              </div>
            ))}
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="flex gap-3 flex-wrap items-center">
          <input value={filters.admin_email} onChange={(e) => { setFilters({ ...filters, admin_email: e.target.value }); setPage(0); }}
            placeholder="Email admin (ej: admin@…)" data-testid="filter-admin-email"
            className="text-sm bg-page border border-carbon-200 rounded px-3 py-2 w-64" />
          <select value={filters.method} onChange={(e) => { setFilters({ ...filters, method: e.target.value }); setPage(0); }}
            data-testid="filter-admin-method"
            className="text-sm bg-page border border-carbon-200 rounded px-3 py-2">
            <option value="">Cualquier método</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
          <input value={filters.path} onChange={(e) => { setFilters({ ...filters, path: e.target.value }); setPage(0); }}
            placeholder="Filtrar por path (regex, ej: campaign)" data-testid="filter-admin-path"
            className="text-sm bg-page border border-carbon-200 rounded px-3 py-2 flex-1 min-w-[200px]" />
          <button onClick={() => reload()} className="text-xs px-3 py-2 border border-carbon-200 rounded hover:bg-carbon-50">Refrescar</button>
        </div>
        <div className="text-xs text-carbon-500 mt-2">
          {data?.total ?? 0} acciones registradas · página {page + 1}
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-carbon-200 text-left">
              <th className="px-5 py-3 section-label">Cuándo</th>
              <th className="px-5 py-3 section-label">Admin</th>
              <th className="px-5 py-3 section-label">Método</th>
              <th className="px-5 py-3 section-label">Endpoint</th>
              <th className="px-5 py-3 section-label">Status</th>
              <th className="px-5 py-3 section-label">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-5 py-10 text-center text-carbon-500">Cargando...</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-carbon-500">Sin acciones registradas aún.</td></tr>
            )}
            {items.map((l) => (
              <tr key={l.id} className="border-b border-carbon-100 hover:bg-page" data-testid={`admin-log-${l.id}`}>
                <td className="px-5 py-3 font-mono text-xs text-carbon-500 whitespace-nowrap">
                  {new Date(l.created_at).toLocaleString("es-GT")}
                </td>
                <td className="px-5 py-3">
                  <div className="font-mono text-xs">{l.admin_email || "—"}</div>
                  {l.admin_role && <div className="text-[10px] text-carbon-500">{l.admin_role}</div>}
                </td>
                <td className="px-5 py-3">
                  <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${methodColor(l.method)}`}>{l.method}</span>
                </td>
                <td className="px-5 py-3 font-mono text-xs break-all">
                  {l.path}
                  {l.resource_id && <span className="text-carbon-400"> · {l.resource_id.slice(0, 12)}</span>}
                </td>
                <td className="px-5 py-3 font-mono text-xs">
                  <span className={l.status_code >= 400 ? "text-signal" : "text-forest"}>{l.status_code}</span>
                  <span className="text-carbon-400 ml-1">· {l.latency_ms}ms</span>
                </td>
                <td className="px-5 py-3 font-mono text-[10px] text-carbon-500">{l.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <div className="flex items-center justify-between mt-3">
        <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
          data-testid="admin-log-prev"
          className="text-xs px-3 py-1.5 border border-carbon-200 rounded hover:bg-carbon-50 disabled:opacity-40">
          ← Anterior
        </button>
        <span className="text-xs text-carbon-500">
          {items.length > 0 ? `${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + items.length}` : "—"} de {data?.total ?? 0}
        </span>
        <button onClick={() => setPage((p) => p + 1)} disabled={items.length < PAGE_SIZE}
          data-testid="admin-log-next"
          className="text-xs px-3 py-1.5 border border-carbon-200 rounded hover:bg-carbon-50 disabled:opacity-40">
          Siguiente →
        </button>
      </div>
    </>
  );
}
